import { useEffect, useState } from 'react';
import { Box, Tag, Heading, Flex, Text, Link as ChakraLink, Stack, Avatar } from '@chakra-ui/react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { snakeCase } from 'lodash';
import { formatDate } from '@/helpers/formatDate';
import { configFile } from '@/config';
import { handleGTMClick } from '@/helpers/handleGTMClick';

interface Props {
  data: any;
}

export default function PostBanner({ data }: Props) {
  const [date, setDate] = useState<String>();

  const { locale } = useRouter();
  const { t } = useTranslation('common');

  useEffect(() => {
    setDate(formatDate(data.updatedAt, locale));
  }, [data]);

  return (
    <Box
      as="section"
      width="100%"
      bgColor="gray.300"
      height={{ base: '500', md: '582' }}
      paddingTop={{ base: '0px', lg: '80px' }}
      paddingBottom={{ base: '0px', lg: '80px' }}
      position="relative"
    >
      <Box
        maxWidth="1216px"
        margin="0 auto"
        display="flex"
        alignItems="flex-end"
        justifyContent="flex-end"
        columnGap="32px"
        width="90%"
      >
        <Box display="flex" flexDirection="column" rowGap="16px">
          {data && (
            <Box width="100%" height="100%" top="0" left="0" position="absolute">
              <Image
                fill
                priority
                style={{ objectFit: 'cover' }}
                placeholder="blur"
                blurDataURL="data:image/webp;base64,UklGRg4DAABXRUJQVlA4WAoAAAAgAAAAvgAAvgAASUNDUMgBAAAAAAHIAAAAAAQwAABtbnRyUkdCIFhZWiAH4AABAAEAAAAAAABhY3NwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAA9tYAAQAAAADTLQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAlkZXNjAAAA8AAAACRyWFlaAAABFAAAABRnWFlaAAABKAAAABRiWFlaAAABPAAAABR3dHB0AAABUAAAABRyVFJDAAABZAAAAChnVFJDAAABZAAAAChiVFJDAAABZAAAAChjcHJ0AAABjAAAADxtbHVjAAAAAAAAAAEAAAAMZW5VUwAAAAgAAAAcAHMAUgBHAEJYWVogAAAAAAAAb6IAADj1AAADkFhZWiAAAAAAAABimQAAt4UAABjaWFlaIAAAAAAAACSgAAAPhAAAts9YWVogAAAAAAAA9tYAAQAAAADTLXBhcmEAAAAAAAQAAAACZmYAAPKnAAANWQAAE9AAAApbAAAAAAAAAABtbHVjAAAAAAAAAAEAAAAMZW5VUwAAACAAAAAcAEcAbwBvAGcAbABlACAASQBuAGMALgAgADIAMAAxADZWUDggIAEAAJATAJ0BKr8AvwA+7WirUC0qqSMrW+ppoB2JaW7dYGkqQFse+iIl/1Vz/NeyboWOqcLshQM5btwvpb6IiYGV2SR2QoGpddi4ASBvKWuUCPnQYQo5l6Ox9Gq/zyVC1kqMq/aLOzgPsLZuSOuwfQUTFiTIefzSo5iDPIm8UMBnmt2X+kBrOKOJ6Wn5kvOFlsLhb7R/0jCACmRPHrvO5eZ7paf57+z6AAD+8N9uHNRKzyz0Utnd8EhNFB7LR03wGbF4+IKR/IzYmN51aXjnZzJk6BX23Mo71z/Zr4lVly8tonev5ls8KCJS87Zf0CpFxa7Z5oq0OVNwWehsFWCszVMH5HCEss1yuhh9SLaOeST7SViVPEJqDYOjU52BPRSEl3zQAA=="
                src={data.mainImage?.data.attributes.url}
                alt={data.title}
              />
              <Box
                display="block"
                width="100%"
                height="100%"
                position="absolute"
                top="0"
                left="0"
                bgGradient="linear-gradient(180deg, rgba(45, 55, 72, 0) 27.39%, #2D3748 100%)"
              />
              <Flex
                height="100%"
                justifyContent="flex-end"
                alignItems="flex-start"
                position="relative"
                width="90%"
                pb="80px"
                maxWidth="1216px"
                margin=" 0 auto"
                flexDirection="column"
              >
                <ChakraLink
                  id={`blog_category_${snakeCase(data.category.data[0].attributes.slug)}`}
                  onClick={handleGTMClick}
                  onAuxClick={handleGTMClick}
                  href={`/blog/categoria/${data.category.data[0].attributes.slug}`}
                  p="0 !important"
                >
                  <Tag
                    bgColor={data.category.data[0].attributes.bgColor}
                    color={data.category.data[0].attributes.color}
                    textTransform="uppercase"
                    mb="24px"
                  >
                    {data.category.data[0].attributes.title}
                  </Tag>
                </ChakraLink>
                <Heading
                  as="h1"
                  maxWidth="1000px"
                  fontSize={{ base: '36px', md: '45px', xl: '60px' }}
                  color={configFile.tertiaryAccentColor}
                  width="100%"
                >
                  {data.title}
                </Heading>
                <Flex m="16px 0 32px 0" gap="16px">
                  <Text color={configFile.tertiaryAccentColor} fontSize="16px" lineHeight="24px">
                    {date}
                  </Text>
                  <Text fontSize="16px" lineHeight="24px" color={configFile.tertiaryAccentColor}>
                    •
                  </Text>
                  <Text fontSize="16px" lineHeight="24px" fontWeight="600" color={configFile.tertiaryAccentColor}>
                    {data.readingTime} {t('article.minRead')}
                  </Text>
                </Flex>

                <Stack alignSelf="stretch" direction="row" justify="flex-start" align="center" spacing="16px">
                  <Avatar
                    width="48px"
                    height="48px"
                    name={data.author.data.attributes.name}
                    src={data.author.data.attributes.img?.data.attributes.url}
                  />
                  <Box>
                    <ChakraLink
                      id={`blog_author_${snakeCase(data.author.data.attributes.url)}`}
                      onClick={handleGTMClick}
                      onAuxClick={handleGTMClick}
                      href={`/blog/perfil/${data.author.data.attributes.url}`}
                      as={Link}
                      fontSize="14px"
                      textDecoration="none"
                      w="auto"
                      p="0 !important"
                      _hover={{
                        textDecoration: 'none',
                      }}
                      fontWeight="500"
                      color={configFile.tertiaryAccentColor}
                    >
                      {data.author.data.attributes.name}
                    </ChakraLink>
                    <Text lineHeight="16px" color="gray.200" fontWeight="400" fontSize="12px">
                      {data.author.data.attributes.jobTitle}
                    </Text>
                  </Box>
                </Stack>
              </Flex>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
